import type { AxiosError } from "axios";

// Global toast service for showing error notifications
// This service will be initialized with the toast context functions
class ToastService {
  private showErrorToast?: (message: string, title?: string) => void;
  private showSuccessToast?: (message: string, title?: string) => void;
  private showWarningToast?: (message: string, title?: string) => void;
  private showInfoToast?: (message: string, title?: string) => void;

  init(toastFunctions: {
    showErrorToast: (message: string, title?: string) => void;
    showSuccessToast: (message: string, title?: string) => void;
    showWarningToast: (message: string, title?: string) => void;
    showInfoToast: (message: string, title?: string) => void;
  }) {
    this.showErrorToast = toastFunctions.showErrorToast;
    this.showSuccessToast = toastFunctions.showSuccessToast;
    this.showWarningToast = toastFunctions.showWarningToast;
    this.showInfoToast = toastFunctions.showInfoToast;
  }

  handleHttpError(error: AxiosError | any) {
    if (!this.showErrorToast) {
      console.warn("ToastService not initialized");
      return;
    }

    let title = "API Error";
    let message = "An unexpected error occurred";

    const status = error.status;
    const responseData = error.response.data;

    switch (status) {
      case 400:
        title = "Bad Request";
        message = responseData?.message || "Invalid request data";
        break;
      case 403:
        title = "Access Denied";
        message = "You don't have permission to access this resource";
        break;
      case 404:
        title = "Not Found";
        message = "The requested resource was not found";
        break;
      case 409:
        title = "Conflict";
        message = responseData?.message || "A conflict occurred with the current state";
        break;
      case 422:
        title = "Validation Error";
        message = responseData?.message || "Please check your input data";
        break;
      case 429:
        title = "Rate Limited";
        message = "Too many requests. Please try again later";
        break;
      case 500:
        title = "Server Error";
        message = "Internal server error. Please try again later";
        break;
      case 502:
        title = "Bad Gateway";
        message = "Service temporarily unavailable";
        break;
      case 503:
        title = "Service Unavailable";
        message = "Service is currently unavailable. Please try again later";
        break;
      case 504:
        title = "Gateway Timeout";
        message = "Request timed out. Please try again";
        break;
      case 522:
        title = "Connection Timeout";
        message = "Request timed out. Please try again";
        break;
      default:
        title = `Error (${status})`;
        message = responseData?.message || `HTTP ${status} error occurred`;
    }

    if (responseData?.message) {
      message = responseData.message;
    } else if (responseData?.error) {
      message = responseData.error;
    } else if (responseData?.errors && Array.isArray(responseData.errors)) {
      message = responseData.errors.join(", ");
    }

    this.showErrorToast(message, title);
  }

  error(message: string, title?: string) {
    this.showErrorToast?.(message, title);
  }

  success(message: string, title?: string) {
    this.showSuccessToast?.(message, title);
  }

  warning(message: string, title?: string) {
    this.showWarningToast?.(message, title);
  }

  info(message: string, title?: string) {
    this.showInfoToast?.(message, title);
  }
}

export const toastService = new ToastService();
